import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Link as LinkIcon } from 'lucide-react';
import { PhotoUploader } from '@/components/PhotoUploader';
import { useLanguage } from '@/contexts/LanguageContext';

interface MultiItemSourcingFormProps {
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  initialData?: any;
}

interface FormItem {
  id: string;
  productName: string;
  description: string;
  quantity: string;
  quantityUnit: string;
  targetPrice: string;
  photos: string[];
  referenceLinks: string[];
}

export function MultiItemSourcingForm({
  onSubmit,
  isSubmitting = false,
  initialData
}: MultiItemSourcingFormProps) {
  const { t } = useLanguage();
  
  // Items state - each item has EVERYTHING
  const [items, setItems] = useState<FormItem[]>(() => {
    if (initialData?.items && initialData.items.length > 0) {
      return initialData.items.map((item: any, idx: number) => ({
        id: String(idx + 1),
        productName: item.productName || item.product_name || '',
        description: item.description || '',
        quantity: String(item.quantity || ''),
        quantityUnit: item.quantityUnit || item.quantity_unit || 'pcs',
        targetPrice: String(item.targetPrice || item.target_price || ''),
        photos: item.photos || [],
        referenceLinks: item.referenceLinks || item.reference_links || []
      }));
    }
    return [
      {
        id: '1',
        productName: '',
        description: '',
        quantity: '',
        quantityUnit: 'pcs',
        targetPrice: '',
        photos: [],
        referenceLinks: []
      }
    ];
  });

  // Per-item link input state
  const [linkInputs, setLinkInputs] = useState<Record<string, { url: string; platform: string }>>({});

  // General request info (applies to entire order)
  const [specialRequirements, setSpecialRequirements] = useState(initialData?.specialRequirements || '');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(initialData?.expectedDeliveryDate || '');
  // Currency is always USD now
  const currency = 'USD';

  // Add new item
  const handleAddItem = () => {
    const newId = String(items.length + 1);
    setItems([...items, {
      id: newId,
      productName: '',
      description: '',
      quantity: '',
      quantityUnit: 'pcs',
      targetPrice: '',
      photos: [],
      referenceLinks: []
    }]);
  };

  // Remove item
  const handleRemoveItem = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  // Update item field
  const handleUpdateItem = (id: string, field: keyof FormItem, value: any) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Update link input for specific item
  const updateLinkInput = (itemId: string, field: 'url' | 'platform', value: string) => {
    setLinkInputs({
      ...linkInputs,
      [itemId]: {
        url: linkInputs[itemId]?.url || '',
        platform: linkInputs[itemId]?.platform || '',
        [field]: value
      }
    });
  };

  // Add reference link to specific item
  const handleAddLinkToItem = (itemId: string) => {
    const input = linkInputs[itemId];
    if (!input || !input.url.trim()) return;

    const linkWithPlatform = input.platform 
      ? `[${input.platform}] ${input.url.trim()}`
      : input.url.trim();
    
    setItems(items.map(item =>
      item.id === itemId 
        ? { ...item, referenceLinks: [...item.referenceLinks, linkWithPlatform] }
        : item
    ));

    // Clear input for this item
    setLinkInputs({ ...linkInputs, [itemId]: { url: '', platform: '' } });
  };

  // Remove reference link from specific item
  const handleRemoveLinkFromItem = (itemId: string, linkIndex: number) => {
    setItems(items.map(item =>
      item.id === itemId 
        ? { ...item, referenceLinks: item.referenceLinks.filter((_, i) => i !== linkIndex) }
        : item
    ));
  };

  // Calculate totals
  const totalQuantity = items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate items - only product name is required
    const validItems = items.filter(item => item.productName.trim() !== '');
    
    if (validItems.length === 0) {
      alert('Please add at least one item with a product name');
      return;
    }

    // Prepare items with ALL per-item data
    const requestItems = validItems.map((item, index) => ({
      item_number: index + 1,
      product_name: item.productName,
      description: item.description,
      quantity: parseInt(item.quantity) || 0,
      quantity_unit: item.quantityUnit,
      target_price: item.targetPrice ? parseFloat(item.targetPrice) : null,
      photos: item.photos,
      reference_links: item.referenceLinks
    }));

    // Submit data
    onSubmit({
      items: requestItems,
      special_requirements: specialRequirements,
      expected_delivery_date: expectedDeliveryDate || null,
      currency: currency,
      status: 'Pending'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Currency Selection - At Top */}
      {/* Currency is now hardcoded to USD - no selector needed */}

      {/* Items Section */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold">{t('items')}</Label>

        <div className="space-y-4">
          {items.map((item, index) => (
            <Card key={item.id} className="border-2 border-gray-200">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="secondary" className="text-base font-semibold px-3 py-1">
                    {t('item')} #{index + 1}
                  </Badge>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('removeItem')}
                    </Button>
                  )}
                </div>

                {/* Product Name */}
                <div className="space-y-2">
                  <Label htmlFor={`product-${item.id}`}>{t('productName')} *</Label>
                  <Input
                    id={`product-${item.id}`}
                    value={item.productName}
                    onChange={(e) => handleUpdateItem(item.id, 'productName', e.target.value)}
                    placeholder={t('productNamePlaceholder')}
                    required
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor={`desc-${item.id}`}>{t('descriptionLabel')}</Label>
                  <Textarea
                    id={`desc-${item.id}`}
                    value={item.description}
                    onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
                    placeholder={t('descriptionPlaceholder')}
                    rows={4}
                  />
                </div>

                {/* Quantity & Unit & Target Price */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`qty-${item.id}`}>{t('quantity')} *</Label>
                    <Input
                      id={`qty-${item.id}`}
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                      placeholder="1000"
                      min="1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`unit-${item.id}`}>{t('unit')} *</Label>
                    <Select
                      value={item.quantityUnit}
                      onValueChange={(value) => handleUpdateItem(item.id, 'quantityUnit', value)}
                    >
                      <SelectTrigger id={`unit-${item.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">pcs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="boxes">boxes</SelectItem>
                        <SelectItem value="sets">sets</SelectItem>
                        <SelectItem value="cartons">cartons</SelectItem>
                        <SelectItem value="pallets">pallets</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`price-${item.id}`}>{t('targetPriceOptional')}</Label>
                    <Input
                      id={`price-${item.id}`}
                      type="number"
                      step="0.01"
                      value={item.targetPrice}
                      onChange={(e) => handleUpdateItem(item.id, 'targetPrice', e.target.value)}
                      placeholder={t('targetPricePlaceholder')}
                      min="0"
                    />
                  </div>
                </div>

                {/* Product Photos for this item */}
                <div className="space-y-2">
                  <PhotoUploader
                    photos={item.photos}
                    onPhotosChange={(photos) => handleUpdateItem(item.id, 'photos', photos)}
                    maxPhotos={10}
                    label={`${t('productPhotos')} (${t('item')} #${index + 1})`}
                    helperText={t('uploadOrDragPhotos')}
                  />
                </div>

                {/* Reference Links for this item */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    {t('referenceLinks')} ({t('item')} #{index + 1})
                  </Label>
                  
                  {/* Add link input */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={linkInputs[item.id]?.platform || "none"}
                        onValueChange={(value) => updateLinkInput(item.id, 'platform', value === "none" ? "" : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectPlatform')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="AliExpress">AliExpress</SelectItem>
                          <SelectItem value="Amazon">Amazon</SelectItem>
                          <SelectItem value="Alibaba">Alibaba</SelectItem>
                          <SelectItem value="DHgate">DHgate</SelectItem>
                          <SelectItem value="eBay">eBay</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        className="col-span-2"
                        value={linkInputs[item.id]?.url || ''}
                        onChange={(e) => updateLinkInput(item.id, 'url', e.target.value)}
                        placeholder={t('pasteProductLink')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddLinkToItem(item.id);
                          }
                        }}
                      />
                    </div>
                    <Button 
                      type="button" 
                      onClick={() => handleAddLinkToItem(item.id)} 
                      size="sm" 
                      variant="outline" 
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('addLink')}
                    </Button>
                  </div>
                  
                  {/* Display existing links */}
                  {item.referenceLinks.length > 0 && (
                    <div className="space-y-2">
                      {item.referenceLinks.map((link, linkIndex) => {
                        const platformMatch = link.match(/^\[(.+?)\]\s*(.+)$/);
                        const platform = platformMatch ? platformMatch[1] : '';
                        const url = platformMatch ? platformMatch[2] : link;
                        
                        return (
                          <div key={linkIndex} className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                            <LinkIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              {platform && (
                                <Badge variant="secondary" className="text-xs mb-1">{platform}</Badge>
                              )}
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline block truncate"
                              >
                                {url}
                              </a>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveLinkFromItem(item.id, linkIndex)}
                              className="text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">{t('addFromPlatforms')}</p>
                </div>

              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Another Item Button at Bottom */}
        <Button type="button" onClick={handleAddItem} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {t('addAnotherItem')}
        </Button>
      </div>

      {/* Summary Card */}
      {items.length > 1 && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-bold text-blue-900">Order Summary</Label>
              <div className="flex items-center gap-6 text-sm font-semibold">
                <div className="text-blue-900">
                  <span className="text-blue-700">Total Items:</span> {items.length}
                </div>
                <div className="text-blue-900">
                  <span className="text-blue-700">Total Units:</span> {totalQuantity}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* General Order Information */}
      <div className="border-t pt-6 space-y-4">
        <Label className="text-lg font-semibold">{t('generalRequestInfo')}</Label>
        <p className="text-sm text-gray-500">{t('appliestoEntireOrder')}</p>

        {/* Special Requirements */}
        <div className="space-y-2">
          <Label htmlFor="specialRequirements">{t('specialRequirementsOptional')}</Label>
          <Textarea
            id="specialRequirements"
            value={specialRequirements}
            onChange={(e) => setSpecialRequirements(e.target.value)}
            placeholder={t('packagingCertifications')}
            rows={4}
          />
        </div>

        {/* Expected Delivery Date */}
        <div className="space-y-2">
          <Label htmlFor="expectedDelivery">{t('expectedDeliveryDate')}</Label>
          <Input
            id="expectedDelivery"
            type="date"
            value={expectedDeliveryDate}
            onChange={(e) => setExpectedDeliveryDate(e.target.value)}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" disabled={isSubmitting} size="lg" className="px-8">
          {isSubmitting ? t('submittingRequest') : t('submitSourcingRequest')}
        </Button>
      </div>
    </form>
  );
}
